import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { contactsAPI } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, MessageCircle, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';

const STEPS = { source: 'source', params: 'params', progress: 'progress', done: 'done' };
const SELECT_ALL_THRESHOLD = 500;

export default function ContactIngestDialog({ open, onOpenChange, onFinished }) {
  const [step, setStep] = useState(STEPS.source);
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [sourceId, setSourceId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [previewItems, setPreviewItems] = useState([]);
  const [previewCursor, setPreviewCursor] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [q, setQ] = useState('');
  const [domain, setDomain] = useState('');
  // **Prendidos por omisión, y al revés uno del otro a propósito.** Lo que alguien espera de una
  // lista de contactos son personas; traer 389 renglones con salas de reunión adentro es una
  // lista que nadie revisa. Los dos se pueden cambiar, y lo que se ocultó se dice siempre.
  const [soloPersonas, setSoloPersonas] = useState(true);
  const [incluirInvitados, setIncluirInvitados] = useState(false);
  const [ocultasTexto, setOcultasTexto] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [run, setRun] = useState(null);
  const pollRef = useRef(null);

  const selectedSource = useMemo(
    () => sources.find((s) => s.source_id === sourceId),
    [sources, sourceId]
  );

  const reset = useCallback(() => {
    setStep(STEPS.source);
    setSourceId('');
    setChannelId('');
    setPreviewItems([]);
    setPreviewCursor(null);
    setPreviewError(null);
    setQ('');
    setDomain('');
    setSelected(new Set());
    setRun(null);
    setConfirmOpen(false);
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    setLoadingSources(true);
    contactsAPI
      .ingestSources()
      .then((res) => setSources(res.data?.sources || []))
      .catch((err) => toast.error(err?.response?.data?.detail || 'No se pudieron cargar orígenes'))
      .finally(() => setLoadingSources(false));
  }, [open, reset]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const loadPreview = async ({ append = false, cursor = null } = {}) => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await contactsAPI.ingestPreview({
        source_id: 'entra_directory',
        q: q || undefined,
        domain: domain || undefined,
        cursor: cursor || undefined,
        solo_personas: soloPersonas,
        incluir_invitados: incluirInvitados,
      });
      const data = res.data || {};
      if (data.error_code) {
        setPreviewError(data.error_message || data.error_code);
      }
      const items = data.items || [];
      setPreviewItems((prev) => (append ? [...prev, ...items] : items));
      setPreviewCursor(data.next_cursor || null);
      setOcultasTexto(data.ocultas_texto || '');
      if (!append) setSelected(new Set());
    } catch (err) {
      setPreviewError(err?.response?.data?.detail || 'Error al listar directorio');
    } finally {
      setPreviewLoading(false);
    }
  };

  const chooseSource = (id) => {
    setSourceId(id);
    setStep(STEPS.params);
    setChannelId('');
    setSelected(new Set());
    setPreviewItems([]);
    setPreviewCursor(null);
    if (id === 'entra_directory') {
      setTimeout(() => loadPreview({ append: false }), 0);
    }
  };

  // **El filtro es del servidor, así que cambiarlo obliga a volver a pedir.** Limpiar la lista
  // sin recargar dejaría la pantalla vacía y se leería como "no hay nadie" — que es justo lo que
  // este cambio vino a evitar. El `cursor` se descarta a propósito: la paginación de Graph es de
  // la consulta anterior y no se puede reusar con otro filtro.
  const yaSeMiroElDirectorio = useRef(false);
  useEffect(() => {
    if (sourceId !== 'entra_directory' || !yaSeMiroElDirectorio.current) {
      yaSeMiroElDirectorio.current = sourceId === 'entra_directory';
      return;
    }
    setPreviewItems([]);
    setPreviewCursor(null);
    setSelected(new Set());
    loadPreview({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soloPersonas, incluirInvitados]);

  const toggleEmail = (email) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const selectPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      previewItems.forEach((i) => next.add(i.email));
      return next;
    });
  };

  // **«Seleccionar todo» tiene que seleccionar todo.** Antes tomaba sólo lo que ya estaba
  // cargado y avisaba con un toast que había que seguir apretando «Cargar más». El resultado
  // real, medido el 2026-08-28: 198 personas importadas de 233, y los 35 que faltaban parecían
  // no existir — Diego buscó a «Diego Ceballos» en la pantalla de Personas y concluyó que el
  // buscador estaba roto. No lo estaba: esas personas nunca se habían importado.
  //
  // Un botón que hace la mitad de lo que dice es peor que uno que no existe, porque el que lo
  // aprieta se queda tranquilo.
  const [trayendoTodo, setTrayendoTodo] = useState(false);

  const selectAllFiltered = async () => {
    setTrayendoTodo(true);
    try {
      const todos = new Set(previewItems.map((i) => i.email));
      let cursor = previewCursor;
      let vueltas = 0;
      // Tope de vueltas: un cursor que no avanza no puede convertirse en un bucle infinito
      // contra Graph. Si se alcanza, se dice — no se calla y se deja creer que trajo todo.
      while (cursor && vueltas < 100) {
        vueltas += 1;
        const res = await contactsAPI.ingestPreview({
          source_id: 'entra_directory',
          q: q || undefined,
          domain: domain || undefined,
          cursor,
          solo_personas: soloPersonas,
          incluir_invitados: incluirInvitados,
        });
        const data = res.data || {};
        (data.items || []).forEach((i) => todos.add(i.email));
        setPreviewItems((prev) => [...prev, ...(data.items || [])]);
        cursor = data.next_cursor || null;
        setPreviewCursor(cursor);
      }
      setSelected(todos);
      if (cursor) {
        toast.warning('El directorio es muy grande', {
          description: `Se seleccionaron ${todos.size} y quedaron más sin recorrer. Afiná la búsqueda.`,
        });
      } else {
        toast.success(`${todos.size} seleccionados — el directorio entero.`);
      }
    } catch (err) {
      toast.error('No se pudo recorrer el directorio completo', {
        description: 'Se seleccionó lo que ya estaba cargado. Probá de nuevo o usá «Cargar más».',
      });
      selectPage();
    } finally {
      setTrayendoTodo(false);
    }
  };

  const canStart = () => {
    if (sourceId === 'whatsapp') return !!channelId;
    if (sourceId === 'entra_directory') return selected.size > 0;
    return false;
  };

  const startIngest = async () => {
    const params =
      sourceId === 'whatsapp'
        ? { channel_id: channelId }
        : { emails: Array.from(selected) };
    try {
      const res = await contactsAPI.ingestStart({ source_id: sourceId, params });
      const runId = res.data?.run_id;
      if (!runId) throw new Error('sin run_id');
      setStep(STEPS.progress);
      setRun({ run_id: runId, status: 'queued', processed: 0, total_planned: 0 });
      pollRef.current = setInterval(async () => {
        try {
          const st = await contactsAPI.ingestRun(runId);
          const doc = st.data;
          setRun(doc);
          if (doc.status === 'completed' || doc.status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setStep(STEPS.done);
            if (doc.status === 'completed') {
              toast.success(
                `Ingesta OK: ${doc.created || 0} creados, ${doc.updated || 0} actualizados`
              );
              onFinished?.();
            } else {
              toast.error(doc.error_message || doc.error_code || 'Ingesta fallida');
            }
          }
        } catch (_) {
          /* keep polling */
        }
      }, 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error('Ya hay una ingesta en curso para este origen');
      } else {
        toast.error(detail || 'No se pudo iniciar la ingesta');
      }
    }
  };

  const progressPct = useMemo(() => {
    if (!run) return 0;
    const total = Number(run.total_planned) || 0;
    const processed = Number(run.processed) || 0;
    if (!total) return run.phase === 'fetching' ? 5 : 0;
    return Math.min(100, Math.round((processed / total) * 100));
  }, [run]);

  const waChannels = selectedSource?.channels || [];
  const domains = selectedSource?.allowed_domains || [];

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v && step === STEPS.progress) return;
          onOpenChange(v);
        }}
      >
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Cargar contactos</DialogTitle>
            <DialogDescription>
              Importá desde WhatsApp o el directorio Microsoft Entra. Se hace merge si la identidad ya existe.
            </DialogDescription>
          </DialogHeader>

          {step === STEPS.source && (
            <div className="space-y-3 py-2">
              {loadingSources ? (
                <div className="flex items-center gap-2 text-sm text-zinc-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Cargando orígenes…
                </div>
              ) : (
                sources.map((s) => (
                  <button
                    key={s.source_id}
                    type="button"
                    onClick={() => chooseSource(s.source_id)}
                    className="w-full text-left border border-brand-border rounded-sm p-4 hover:bg-zinc-50 transition-colors"
                  >
                    <div className="flex items-center gap-2 font-medium text-sm">
                      {s.source_id === 'whatsapp' ? (
                        <MessageCircle className="w-4 h-4" />
                      ) : (
                        <Building2 className="w-4 h-4" />
                      )}
                      {s.title}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">{s.description}</p>
                  </button>
                ))
              )}
            </div>
          )}

          {step === STEPS.params && sourceId === 'whatsapp' && (
            <div className="space-y-3 py-2">
              <Label className="text-xs">Canal WhatsApp</Label>
              {waChannels.length === 0 ? (
                <p className="text-sm text-amber-700">No hay canales WhatsApp. Creá uno en Canales.</p>
              ) : (
                <div className="space-y-2">
                  {waChannels.map((ch) => {
                    const st = String(ch.bridge_status || ch.status || '').toLowerCase();
                    const isOk = ['connected', 'open', 'active'].includes(st);
                    return (
                      <label
                        key={ch.channel_id}
                        className={`flex items-center gap-3 border rounded-sm p-3 cursor-pointer ${
                          channelId === ch.channel_id ? 'border-zinc-900' : 'border-brand-border'
                        }`}
                      >
                        <input
                          type="radio"
                          name="wa-channel"
                          checked={channelId === ch.channel_id}
                          onChange={() => setChannelId(ch.channel_id)}
                        />
                        <div className="text-sm">
                          <div className="font-medium">{ch.name || ch.channel_id}</div>
                          <div className="text-xs text-zinc-500">
                            {ch.phone_number || ch.bridge_phone || 'sin número'} · mongo: {ch.status || '?'}
                            {ch.bridge_status ? ` · bridge: ${ch.bridge_status}` : ''}
                            {!isOk ? ' — si bridge ≠ open, la carga intentará restaurar la sesión' : ''}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-zinc-500">
                Se importará la agenda/chats 1:1 de ese número (sin grupos). Tras un deploy el bridge
                puede quedar sin sesión en memoria aunque OmniSyS diga conectado: al cargar se intenta
                restaurar con las credenciales guardadas.
              </p>
              <DialogFooter className="gap-2">
                <Button variant="outline" className="rounded-sm" onClick={() => setStep(STEPS.source)}>
                  Atrás
                </Button>
                <Button
                  className="rounded-sm"
                  disabled={!canStart()}
                  onClick={() => setConfirmOpen(true)}
                >
                  Continuar
                </Button>
              </DialogFooter>
            </div>
          )}

          {step === STEPS.params && sourceId === 'entra_directory' && (
            <div className="space-y-3 py-2">
              {domains.length === 0 ? (
                <p className="text-sm text-amber-700">
                  No hay dominios allowlist. Configuralos en Equipo → identidad Entra.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-1">
                    {domains.map((d) => (
                      <Badge
                        key={d}
                        variant={domain === d ? 'default' : 'outline'}
                        className="rounded-sm cursor-pointer"
                        onClick={() => setDomain(domain === d ? '' : d)}
                      >
                        {d}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      className="rounded-sm"
                      placeholder="Buscar nombre o email"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && loadPreview({ append: false })}
                    />
                    <Button
                      variant="outline"
                      className="rounded-sm"
                      onClick={() => loadPreview({ append: false })}
                      disabled={previewLoading}
                    >
                      Buscar
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-1">
                    <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
                      <Checkbox
                        checked={soloPersonas}
                        onCheckedChange={(v) => setSoloPersonas(!!v)}
                        data-testid="ingest-solo-personas"
                      />
                      Sólo personas
                      <span className="text-zinc-400">— sin buzones de área, salas ni equipos</span>
                    </label>
                    <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
                      <Checkbox
                        checked={incluirInvitados}
                        onCheckedChange={(v) => setIncluirInvitados(!!v)}
                        data-testid="ingest-incluir-invitados"
                      />
                      Incluir invitados externos
                      <span className="text-zinc-400">— gente de otras empresas</span>
                    </label>
                  </div>
                  {/* **Ocultar sin decirlo manda a buscar a alguien que sí está en el directorio.**
                      Por eso el filtro informa cuántas sacó y por qué motivo. */}
                  {ocultasTexto && (
                    <p className="text-xs text-zinc-500" data-testid="ingest-ocultas">
                      {ocultasTexto} <span className="text-zinc-400">Destildá las casillas para verlas.</span>
                    </p>
                  )}
                  {previewError && <p className="text-xs text-red-600">{previewError}</p>}
                  <div className="flex gap-2 flex-wrap">
                    <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={selectPage}>
                      Seleccionar página
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={selectAllFiltered}>
                      {trayendoTodo ? 'Trayendo el directorio…' : 'Seleccionar todo el directorio'}
                    </Button>
                    <span className="text-xs text-zinc-500 self-center">{selected.size} seleccionados</span>
                  </div>
                  <div className="border border-brand-border rounded-sm max-h-64 overflow-y-auto divide-y">
                    {previewLoading && previewItems.length === 0 ? (
                      <div className="p-4 text-sm flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Cargando directorio…
                      </div>
                    ) : previewItems.length === 0 ? (
                      <div className="p-4 text-sm text-zinc-500">Sin resultados</div>
                    ) : (
                      previewItems.map((item) => (
                        <label key={item.email} className="flex items-start gap-2 p-2 text-sm cursor-pointer hover:bg-zinc-50">
                          <Checkbox
                            checked={selected.has(item.email)}
                            onCheckedChange={() => toggleEmail(item.email)}
                          />
                          <div>
                            <div className="font-medium">{item.display_name}</div>
                            <div className="text-xs text-zinc-500">{item.email}</div>
                            {(item.department || item.job_title) && (
                              <div className="text-[11px] text-zinc-400">
                                {[item.job_title, item.department].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                  {previewCursor && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      disabled={previewLoading}
                      onClick={() => loadPreview({ append: true, cursor: previewCursor })}
                    >
                      {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Cargar más
                    </Button>
                  )}
                </>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" className="rounded-sm" onClick={() => setStep(STEPS.source)}>
                  Atrás
                </Button>
                <Button
                  className="rounded-sm"
                  disabled={!canStart()}
                  onClick={() => {
                    if (selected.size >= SELECT_ALL_THRESHOLD) {
                      toast.message('Confirmá la carga masiva', {
                        description: `${selected.size} contactos. Revisá el resumen antes de confirmar.`,
                      });
                    }
                    setConfirmOpen(true);
                  }}
                >
                  Continuar
                </Button>
              </DialogFooter>
            </div>
          )}

          {(step === STEPS.progress || step === STEPS.done) && run && (
            <div className="space-y-3 py-2">
              <div className="text-sm">
                {run.status === 'failed' ? (
                  <span className="text-red-600">{run.error_message || run.error_code}</span>
                ) : run.status === 'completed' ? (
                  <span>Completado</span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {run.phase === 'fetching'
                      ? 'Obteniendo contactos…'
                      : `Creando/actualizando… ${run.processed || 0}/${run.total_planned || '—'}`}
                  </span>
                )}
              </div>
              <Progress value={progressPct} className="h-2" />
              <div className="grid grid-cols-3 gap-2 text-xs text-zinc-600">
                <div>Creados: {run.created || 0}</div>
                <div>Actualizados: {run.updated || 0}</div>
                <div>Omitidos: {run.skipped || 0}</div>
              </div>
              {Array.isArray(run.errors) && run.errors.length > 0 && (
                <div className="text-xs text-amber-700 max-h-24 overflow-y-auto">
                  {run.errors.slice(0, 5).map((e, i) => (
                    <div key={i}>{e.external_id}: {e.error}</div>
                  ))}
                </div>
              )}
              {step === STEPS.done && (
                <DialogFooter>
                  <Button className="rounded-sm" onClick={() => onOpenChange(false)}>
                    Cerrar
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmActionDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Confirmar carga de contactos"
        description={
          sourceId === 'whatsapp'
            ? `Se importará la agenda del canal seleccionado (sin grupos). Merge si el teléfono ya existe.`
            : `Se importarán ${selected.size} contactos del directorio Entra (dominios del tenant). Merge si el email ya existe.`
        }
        confirmText="Iniciar carga"
        onConfirm={async () => {
          await startIngest();
        }}
      />
    </>
  );
}

export function IngestSourceBadges({ ingestion }) {
  const sources = ingestion?.sources;
  if (!Array.isArray(sources) || sources.length === 0) return null;
  const label = (s) => (s === 'whatsapp' ? 'WhatsApp' : s === 'entra_directory' ? 'Entra' : s);
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {sources.map((s) => (
        <Badge key={s} variant="outline" className="rounded-sm text-[10px]">
          {label(s)}
        </Badge>
      ))}
    </div>
  );
}
